// Must stay first: patches runtime APIs missing on older Safari (pdfjs 4.4
// calls Promise.withResolvers at module init). See polyfills.ts.
import './polyfills';
import React from 'react';
import { FaroErrorBoundary } from '@grafana/faro-react';
import { createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles';
import { SecurityProvider } from '@skyslope/auth-react';
import mache from '@skyslope/mache';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { enableAllPlugins } from 'immer';
import pdfjsWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';
// eslint-disable-next-line import/no-named-as-default -- posthog-js re-exports its own default as a named `posthog` too
import posthog from 'posthog-js';
import ReactDOM from 'react-dom';
import TagManager from 'react-gtm-module';
import { pdfjs } from 'react-pdf';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import './index.css';

enableAllPlugins();
import App from './app';
import { authReady, getUserManager } from './auth/oktaConfig';
import SecurityEffects from './auth/SecurityEffects';
import { LaunchDarklyProvider } from './common/launchDarkly';
import TimeoutModalWrapper from './common/TimeoutModalWrapper';
import { MobileAppFunctions } from './components/mobileAppFunctions/MobileAppFunctions';
import { PageFrame } from './containers/PageFrame';
import HeaderContextProvider from './context/header-context';
import { GOOGLE_TAG_MANAGER_ID } from './lib/environment-config';
import { isLegacyBrowser } from './lib/legacy-browser';
// Imported here (early) so tokens are removed from the URL before sending
// data to Grafana Faro.
import './lib/faro';
import {
  IS_DEV,
  isPostHogConfigured,
  POSTHOG_DISABLED,
  POSTHOG_HOST,
  POSTHOG_PROJECT_TOKEN,
  registerAnalyticsProperties,
} from './lib/posthog';
import configureStore from './store/index';
import { RetainFrameOrigin } from './utils/retain-frame-origin';

if (!isPostHogConfigured) {
  if (IS_DEV && !POSTHOG_DISABLED) {
    throw new Error(
      'POSTHOG_PROJECT_TOKEN is missing or still an entrypoint placeholder, so PostHog events are being silently dropped. This error stops appearing once a real value is set.'
    );
  }
} else {
  posthog.init(POSTHOG_PROJECT_TOKEN, {
    api_host: POSTHOG_HOST,
    defaults: '2026-01-30',
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    person_profiles: 'identified_only',
    autocapture: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    session_recording: {
      maskAllInputs: true,
      maskInputFn: (text, element) =>
        element?.getAttribute('data-ph-unmask') === 'true'
          ? text
          : '*'.repeat(typeof text === 'string' ? text.length : 0),
    },
  });
  registerAnalyticsProperties();
}

TagManager.initialize({
  gtmId: 'GTM-56H38PR',
  auth: GOOGLE_TAG_MANAGER_ID,
});

// Bundled same-origin so rendering doesn't depend on unpkg.com (blocked by some
// corporate proxies/ad-blockers), and so the real worker can start — cross-origin
// worker scripts are refused by browsers, forcing pdf.js onto its main-thread fallback.
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

const store = configureStore();

// @ts-expect-error -- This code was written prior to enforcing lint rules
const theme = createMuiTheme(mache);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const Providers = (
  <FaroErrorBoundary fallback={<>An error has occurred</>}>
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <MuiThemeProvider theme={theme}>
          <Router>
            <RetainFrameOrigin />
            <HeaderContextProvider>
              <TimeoutModalWrapper />
              <MobileAppFunctions />
              <SecurityProvider userManager={getUserManager()}>
                <LaunchDarklyProvider>
                  <SecurityEffects />
                  <PageFrame>
                    <App />
                  </PageFrame>
                </LaunchDarklyProvider>
              </SecurityProvider>
            </HeaderContextProvider>
          </Router>
        </MuiThemeProvider>
      </Provider>
    </QueryClientProvider>
  </FaroErrorBoundary>
);

/**
 * On legacy browsers (see legacy-browser.ts), run pdf.js on the main thread:
 * Safari 15 silently never executes pdfjs's module worker (constructor
 * succeeds, module never runs, no error event), so pdf.js waits forever and
 * every PDF stays on its loading state. Pre-importing the worker module and
 * exposing it as globalThis.pdfjsWorker makes pdf.js use its supported
 * main-thread mode instead of spawning a worker. structuredClone is wrapped
 * to drop the `transfer` argument: pdf.js's same-thread loopback port
 * transfers buffers the sender still needs, which detaches them under
 * polyfilled implementations — cloning without transfer is semantically
 * identical on a single thread.
 */
async function bootstrap() {
  if (isLegacyBrowser) {
    const nativeStructuredClone = globalThis.structuredClone.bind(globalThis);
    globalThis.structuredClone = ((value: unknown) => nativeStructuredClone(value)) as typeof structuredClone;
    // @ts-expect-error -- the pdfjs worker module ships no type declarations
    globalThis.pdfjsWorker = await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs');
  }
  // In a widget token session, wait until a token left over from an earlier user is gone (AP-3360).
  await authReady;
  ReactDOM.render(Providers, document.getElementById('root'));
}

void bootstrap();
